// src/pages/PlanEssential.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  ShieldCheck,
  Sparkles,
  WalletCards,
} from "lucide-react";

import "../sass/pages/plan-essential.scss";
import Logo from "../assets/logoClaro.webp";

const PLAN = {
  id: "essencial",
  name: "PsyAssist Essencial",
  price: "R$ 29,97",
  pricePayload: "R$ 29,97/MÊS",
  normalPrice: "R$ 49,97",
  normalPricePayload: "R$ 49,97/MÊS",
  priceId:
    process.env.REACT_APP_STRIPE_PRICE_ESSENCIAL ||
    "price_1U18NBKKOfm7LsUJzf5rztCc",
  promotionalOffer: true,
  promotionalLabel:
    "Preço promocional de lançamento",
  trialDays: 7,
};

const FEATURES = [
  "Cadastro e organização de pacientes",
  "Prontuário e histórico de evoluções",
  "Anamnese",
  "Agenda e agendamentos recorrentes",
  "Controle de sessões e pagamentos",
  "Receitas, despesas e relatórios básicos",
  "Gestão de leads",
  "Controle de pagamentos não realizados",
  "Apoio à organização administrativa e fiscal",
];

const SELECTED_PLAN_STORAGE_KEY =
  "psyassist:selected-plan";

const PlanEssential = () => {
  const navigate = useNavigate();

  const handleSubscribe = () => {
    if (!PLAN.id || !PLAN.priceId) {
      console.error(
        "Plano Essencial inválido ou sem priceId.",
        PLAN
      );

      return;
    }

    const payload = {
      planId: PLAN.id,

      planName: PLAN.name,

      planPriceIndividual:
        PLAN.pricePayload,

      planPriceClinica:
        PLAN.pricePayload,

      planNormalPriceIndividual:
        PLAN.normalPricePayload,

      planNormalPriceClinica:
        PLAN.normalPricePayload,

      priceId: PLAN.priceId,

      tipoUsuario: "individual",

      promotionalOffer:
        PLAN.promotionalOffer === true,

      promotionalLabel:
        PLAN.promotionalLabel,

      trialDays: PLAN.trialDays,

      selectedAt:
        new Date().toISOString(),
    };

    try {
      sessionStorage.setItem(
        SELECTED_PLAN_STORAGE_KEY,
        JSON.stringify(payload)
      );

      sessionStorage.setItem(
        "selectedPlan",
        JSON.stringify(payload)
      );
    } catch (error) {
      console.error(
        "Não foi possível salvar o plano Essencial na sessão:",
        error
      );
    }

    console.log(
      `Plano selecionado: ${PLAN.name}`,
      payload
    );

    navigate(
      "/termos",
      {
        state: payload,
      }
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <main className="plan-detail plan-detail--essential">
      <div
        className="plan-detail__background"
        aria-hidden="true"
      >
        <span className="plan-detail__orb plan-detail__orb--one" />
        <span className="plan-detail__orb plan-detail__orb--two" />
        <span className="plan-detail__grid" />
      </div>

      <div className="plan-detail__shell">
        <aside className="plan-detail__summary">
          <div className="plan-detail__brand">
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Gestão profissional para psicólogos
              </span>
            </div>
          </div>

          <div className="plan-detail__summary-content">
            <span className="plan-detail__kicker">
              <WalletCards size={15} />
              Plano Essencial
            </span>

            <h1>
              Organize sua prática com uma base simples e completa.
            </h1>

            <p>
              Centralize pacientes, agenda, sessões e financeiro em um único
              workspace, reduzindo controles paralelos e tarefas manuais.
            </p>

            <div className="plan-detail__price">
              <span>
                Preço promocional de lançamento
              </span>

              <strong>
                {PLAN.price}
                <small>/mês</small>
              </strong>

              <p>
                Depois do lançamento:{" "}
                <b>
                  {PLAN.normalPrice}/mês
                </b>
              </p>
            </div>
          </div>

          <div className="plan-detail__aside-note">
            <ShieldCheck size={19} />

            <div>
              <strong>
                Sem fidelidade
              </strong>

              <span>
                Você poderá alterar o plano posteriormente conforme sua rotina
                evoluir.
              </span>
            </div>
          </div>
        </aside>

        <section className="plan-detail__panel">
          <header className="plan-detail__panel-head">
            <div className="plan-detail__panel-icon">
              <WalletCards size={22} />
            </div>

            <div>
              <span>
                Profissional individual
              </span>

              <h2>
                PsyAssist Essencial
              </h2>

              <p>
                A estrutura principal para organizar pacientes, agenda,
                sessões e financeiro.
              </p>
            </div>
          </header>

          <section className="plan-detail__trial">
            <div className="plan-detail__trial-icon">
              <Sparkles size={19} />
            </div>

            <div>
              <span>
                Período de avaliação
              </span>

              <strong>
                7 dias grátis
              </strong>

              <p>
                O cartão é solicitado no cadastro, mas a cobrança mensal só
                começa após o período de teste se a assinatura for mantida.
              </p>
            </div>
          </section>

          <section className="plan-detail__features">
            <div className="plan-detail__section-title">
              <span>
                Recursos incluídos
              </span>

              <strong>
                Tudo o que você precisa para começar
              </strong>
            </div>

            <ul>
              {FEATURES.map((feature) => (
                <li key={feature}>
                  <CheckCircle2
                    size={17}
                    aria-hidden="true"
                  />

                  <span>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="plan-detail__checkout-info">
            <article>
              <CalendarDays size={18} />

              <div>
                <span>
                  Primeiro ciclo
                </span>

                <strong>
                  7 dias sem cobrança
                </strong>
              </div>
            </article>

            <article>
              <CreditCard size={18} />

              <div>
                <span>
                  Após o teste
                </span>

                <strong>
                  {PLAN.price}/mês
                </strong>
              </div>
            </article>

            <article>
              <BadgeCheck size={18} />

              <div>
                <span>
                  Condição
                </span>

                <strong>
                  Sem fidelidade
                </strong>
              </div>
            </article>
          </section>

          <div className="plan-detail__actions">
            <button
              type="button"
              className="plan-detail__primary"
              onClick={handleSubscribe}
            >
              Começar com o Essencial
              <ArrowRight size={18} />
            </button>

            <button
              type="button"
              className="plan-detail__secondary"
              onClick={handleBack}
            >
              <ArrowLeft size={17} />
              Voltar
            </button>
          </div>

          <footer className="plan-detail__footnote">
            <ShieldCheck size={15} />

            <p>
              Condição promocional válida enquanto a oferta de lançamento
              estiver ativa. O PsyAssist é uma ferramenta de organização e
              apoio profissional.
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default PlanEssential;
